import { createContext, useContext, useEffect, useState } from 'react'

interface GeoPrice {
  offerPrice: string
  originalPrice: string
  currency: string
  symbol: string
  label: string
  loading: boolean
}

const PRICE_MAP: Record<
  string,
  {
    offer: string
    original: string
    symbol: string
    currency: string
    label: string
  }
> = {
  MX: {
    offer: '109',
    original: '1,669',
    symbol: '$',
    currency: 'MXN',
    label: 'MXN',
  },
  CO: {
    offer: '26,800',
    original: '407,000',
    symbol: '$',
    currency: 'COP',
    label: 'COP',
  },
  AR: {
    offer: '6,390',
    original: '97,000',
    symbol: '$',
    currency: 'ARS',
    label: 'ARS',
  },
  CL: {
    offer: '5,999',
    original: '91,000',
    symbol: '$',
    currency: 'CLP',
    label: 'CLP',
  },
  PE: {
    offer: '23.99',
    original: '364',
    symbol: 'S/',
    currency: 'PEN',
    label: 'PEN',
  },
  BR: {
    offer: '31.99',
    original: '485',
    symbol: 'R$',
    currency: 'BRL',
    label: 'BRL',
  },
  ES: {
    offer: '5.89',
    original: '89',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  FR: {
    offer: '5.89',
    original: '89',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  DE: {
    offer: '5.89',
    original: '89',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  IT: {
    offer: '5.89',
    original: '89',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  PT: {
    offer: '5.89',
    original: '89',
    symbol: '€',
    currency: 'EUR',
    label: 'EUR',
  },
  GB: {
    offer: '4.99',
    original: '76',
    symbol: '£',
    currency: 'GBP',
    label: 'GBP',
  },
  EC: {
    offer: '6.39',
    original: '97',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  PA: {
    offer: '6.39',
    original: '97',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  SV: {
    offer: '6.39',
    original: '97',
    symbol: '$',
    currency: 'USD',
    label: 'USD',
  },
  GT: {
    offer: '49.90',
    original: '749',
    symbol: 'Q',
    currency: 'GTQ',
    label: 'GTQ',
  },
  CR: {
    offer: '3,299',
    original: '49,990',
    symbol: '₡',
    currency: 'CRC',
    label: 'CRC',
  },
  DO: {
    offer: '379',
    original: '5,749',
    symbol: 'RD$',
    currency: 'DOP',
    label: 'DOP',
  },
  UY: {
    offer: '259',
    original: '3,929',
    symbol: '$',
    currency: 'UYU',
    label: 'UYU',
  },
  BO: {
    offer: '43.99',
    original: '669',
    symbol: 'Bs',
    currency: 'BOB',
    label: 'BOB',
  },
  PY: {
    offer: '47,900',
    original: '726,000',
    symbol: '₲',
    currency: 'PYG',
    label: 'PYG',
  },
  HN: {
    offer: '159',
    original: '2,399',
    symbol: 'L',
    currency: 'HNL',
    label: 'HNL',
  },
  NI: {
    offer: '234',
    original: '3,559',
    symbol: 'C$',
    currency: 'NIO',
    label: 'NIO',
  },
  VE: {
    offer: '234',
    original: '3,559',
    symbol: 'Bs',
    currency: 'VES',
    label: 'VES',
  },
}

const DEFAULT_PRICE: GeoPrice = {
  offerPrice: '6.39',
  originalPrice: '97',
  symbol: '$',
  currency: 'USD',
  label: 'USD',
  loading: true,
}

export const GeoPriceContext = createContext<GeoPrice>(DEFAULT_PRICE)

export function useGeoPrice(): GeoPrice {
  return useContext(GeoPriceContext)
}

export function useGeoPriceLoader(): GeoPrice {
  const [geoPrice, setGeoPrice] = useState<GeoPrice>(DEFAULT_PRICE)

  useEffect(() => {
    const cached = sessionStorage.getItem('las3jarras_geo')
    if (cached) {
      try {
        setGeoPrice({ ...JSON.parse(cached), loading: false })
        return
      } catch {}
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        clearTimeout(timeout)
        const country = data?.country_code || 'US'
        const mapped = PRICE_MAP[country]

        const result: GeoPrice = mapped
          ? {
              offerPrice: mapped.offer,
              originalPrice: mapped.original,
              symbol: mapped.symbol,
              currency: mapped.currency,
              label: mapped.label,
              loading: false,
            }
          : { ...DEFAULT_PRICE, loading: false }

        sessionStorage.setItem('las3jarras_geo', JSON.stringify(result))
        setGeoPrice(result)
      })
      .catch(() => {
        clearTimeout(timeout)
        setGeoPrice({ ...DEFAULT_PRICE, loading: false })
      })

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [])

  return geoPrice
}
